import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";

import { AuthCoverLink, AuthShell } from "@/components/shared/AuthShell";
import { LoginForm } from "@/components/login-form";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import { getLandingPrivacyUrl, getLandingTermsUrl } from "@/lib/landingLinks";
import { getSupportMailtoHref } from "@/lib/support";

const ALLOWED_RETURN_PREFIXES = ["/accept-invitation", "/accept-request-invite"];

function getReturnTo(searchParams: URLSearchParams): string {
  const returnTo = searchParams.get("returnTo");
  if (!returnTo || typeof returnTo !== "string") return "/";
  const path = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
  const allowed = ALLOWED_RETURN_PREFIXES.some((prefix) =>
    path === prefix || path.startsWith(`${prefix}?`)
  );
  return allowed ? path : "/";
}

/**
 * What the cover half says for each audience.
 *
 * The half only earns its place if it REACTS: static it is a poster, driven by
 * the hovered or focused role card it previews the choice about to be made.
 * The professional entry is also the single place the invitation-only rule
 * (decision e7984b6) is stated, which is what stops a professional hunting for
 * a sign-up link that does not exist.
 */
const COVER = {
  neutral: {
    title: "auth.login.cover.neutralTitle",
    body: "auth.login.cover.neutralBody",
    note: "auth.login.cover.neutralNote",
  },
  user: {
    title: "auth.login.cover.userTitle",
    body: "auth.login.cover.userBody",
    note: "auth.login.cover.userNote",
  },
  professional: {
    title: "auth.login.cover.professionalTitle",
    body: "auth.login.cover.professionalBody",
    note: "auth.login.cover.professionalNote",
  },
} as const;

export function LoginPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [preview, setPreview] = useState<"user" | "professional" | null>(null);
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  const returnTo = getReturnTo(searchParams);

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-auth-surface">
        <LoadingLogo size="lg" className="text-primary-foreground" />
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  const copy = COVER[preview ?? "neutral"];

  /*
    FULL-BLEED 50/50, not a card on a canvas (decision ab0acd1). The old shell
    floated a card on the navy, which always left background reading as empty —
    the complaint that started this. Here the navy IS the cover half, so there
    is no leftover by construction, and the form sits on a plain surface where
    the code boxes stay legible.
  */
  return (
    <AuthShell
      cover={{
        title: t(copy.title),
        body: t(copy.body),
        /* Trans, not t(): the neutral note carries a <support> tag so
           "Escribinos" actually reaches support. A note with no tags renders
           unchanged, so the other two roles pay nothing for it. */
        note: (
          <Trans
            i18nKey={copy.note}
            components={{
              support: <AuthCoverLink href={getSupportMailtoHref()} />,
            }}
          />
        ),
      }}
      footer={
        <Trans
          i18nKey="auth.login.legalAgreement"
          components={{
            terms: (
              <a
                href={getLandingTermsUrl(locale)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              />
            ),
            privacy: (
              <a
                href={getLandingPrivacyUrl(locale)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              />
            ),
          }}
        />
      }
    >
      <LoginForm
        returnTo={returnTo}
        showLoginTypeSelector
        chrome="bare"
        onRolePreview={setPreview}
      />
    </AuthShell>
  );
}
